import { createFileRoute, Link } from "@tanstack/react-router";
import { gatedRobotsMeta } from "@/lib/seo-gating";
import { Layers, ArrowLeft, Wind, Sparkles, Activity } from "lucide-react";
import { BookmarkButton } from "@/components/learn/BookmarkButton";
import { LearnCarousel, LEARN_THEMES, type LearnCard } from "@/components/learn/LearnCarousel";
import {
  PV_INTRO,
  PV_STATES,
  PV_CLIMB,
  PV_NEUROCEPTION,
  PV_COMPASSION,
  PV_REFLECT,
} from "@/data/learn/polyvagal";

export const Route = createFileRoute("/learn/polyvagal-ladder")({
  head: () => ({
    meta: [
      { title: "The Polyvagal Ladder — Recovery Trauma™" },
      { name: "description", content: "Ventral, sympathetic and dorsal explained gently. A trauma-informed guide to the polyvagal ladder and small steps that help you climb back into safety." },
      { property: "og:title", content: "The Polyvagal Ladder — Recovery Trauma™" },
      { property: "og:description", content: "Safe and social, mobilised, or shut down — learn to name the rung you're on and one small thing that helps you move." },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "https://recoverytrauma.com/learn/polyvagal-ladder" },
      { name: "twitter:card", content: "summary_large_image" },
      ...gatedRobotsMeta("/learn/polyvagal-ladder"),
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/learn/polyvagal-ladder" }],
  }),
  component: PolyvagalLadderPage,
});

function LadderDiagram() {
  return (
    <svg
      viewBox="0 0 400 240"
      role="img"
      aria-label="A ladder of three nervous system states: ventral vagal safe and social at the top, sympathetic mobilised in the middle, dorsal vagal shutdown at the bottom."
      className="mx-auto h-auto w-full max-w-sm rounded-2xl"
    >
      <rect x="0" y="0" width="400" height="70" rx="10" fill="hsl(160 45% 90%)" />
      <text x="20" y="32" fill="hsl(165 45% 22%)" fontSize="14" fontWeight="700">🌤️ Ventral vagal</text>
      <text x="20" y="52" fill="hsl(165 30% 30%)" fontSize="11">Safe · Social · Connected</text>

      <rect x="0" y="85" width="400" height="70" rx="10" fill="hsl(25 75% 91%)" />
      <text x="20" y="117" fill="hsl(20 60% 32%)" fontSize="14" fontWeight="700">🔥 Sympathetic</text>
      <text x="20" y="137" fill="hsl(20 40% 36%)" fontSize="11">Mobilised · Fight or flight</text>

      <rect x="0" y="170" width="400" height="70" rx="10" fill="hsl(225 30% 89%)" />
      <text x="20" y="202" fill="hsl(225 35% 28%)" fontSize="14" fontWeight="700">🌑 Dorsal vagal</text>
      <text x="20" y="222" fill="hsl(225 20% 34%)" fontSize="11">Shutdown · Collapse · Numb</text>
    </svg>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="space-y-1.5 text-start">
      {items.map((s) => (
        <li key={s} className="relative ps-5 before:absolute before:start-0 before:content-['•']">
          {s}
        </li>
      ))}
    </ul>
  );
}

function PolyvagalLadderPage() {
  const theme = LEARN_THEMES.sage;

  const cards: LearnCard[] = [
    {
      id: "intro",
      emoji: "🪜",
      chip: "Intro",
      title: "The Polyvagal Ladder",
      body: (
        <>
          <p>{PV_INTRO}</p>
          <LadderDiagram />
        </>
      ),
    },
    ...PV_STATES.map((s) => ({
      id: s.id,
      emoji: s.emoji,
      chip: s.strapline,
      title: s.name,
      body: (
        <>
          <p>{s.story}</p>
          <BulletList items={[...s.signs]} />
          <p className="pt-1 text-center italic text-muted-foreground">{s.voice}</p>
        </>
      ),
    })),
    {
      id: "neuroception",
      emoji: "🧠",
      chip: "Why",
      title: "Neuroception — deciding before you think",
      body: <p>{PV_NEUROCEPTION}</p>,
    },
    ...PV_CLIMB.map((c, i) => ({
      id: `climb-${i}`,
      emoji: i === 0 ? "🌅" : i === 1 ? "🌬️" : "✨",
      chip: "Climbing",
      title: c.label,
      body: (
        <>
          <p className="text-center italic text-muted-foreground">{c.note}</p>
          <BulletList items={c.steps} />
        </>
      ),
    })),
    {
      id: "compassion",
      emoji: "💛",
      chip: "Kindness",
      title: "There is no bad rung",
      body: <p>{PV_COMPASSION}</p>,
    },
    {
      id: "practise",
      emoji: "🌿",
      chip: "Practise",
      title: "Tools that help you climb",
      body: (
        <>
          <p className="text-center">These practices inside Recovery Trauma™ work with the body, where state change actually happens.</p>
          <div className="flex flex-wrap justify-center gap-2 pt-2">
            <Link to="/breathing" className="inline-flex items-center gap-1.5 rounded-full bg-white/70 px-3 py-1.5 text-sm text-foreground hover:bg-white dark:bg-black/30 dark:hover:bg-black/50">
              <Wind className="h-3.5 w-3.5" /> Breathing
            </Link>
            <Link to="/grounding" className="inline-flex items-center gap-1.5 rounded-full bg-white/70 px-3 py-1.5 text-sm text-foreground hover:bg-white dark:bg-black/30 dark:hover:bg-black/50">
              <Activity className="h-3.5 w-3.5" /> Grounding
            </Link>
            <Link to="/tapping" className="inline-flex items-center gap-1.5 rounded-full bg-white/70 px-3 py-1.5 text-sm text-foreground hover:bg-white dark:bg-black/30 dark:hover:bg-black/50">
              <Sparkles className="h-3.5 w-3.5" /> EFT tapping
            </Link>
            <Link to="/learn/window-of-tolerance" className="inline-flex items-center gap-1.5 rounded-full bg-white/70 px-3 py-1.5 text-sm text-foreground hover:bg-white dark:bg-black/30 dark:hover:bg-black/50">
              <Sparkles className="h-3.5 w-3.5" /> Window of Tolerance
            </Link>
          </div>
        </>
      ),
    },
    {
      id: "reflect",
      emoji: "💭",
      chip: "Reflection",
      title: "Which rung are you on right now?",
      body: (
        <ul className="space-y-2 text-start">
          {PV_REFLECT.map((q) => (
            <li key={q}>{q}</li>
          ))}
        </ul>
      ),
    },
  ];

  return (
    <main className={`min-h-dvh px-4 py-10 ${theme.pageBg}`}>
      <div className="mx-auto max-w-3xl">
        <Link to="/learn" className="mb-6 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Back to Learn
        </Link>

        <header className="mb-8 text-center">
          <div className={`mb-3 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs ${theme.chipBg} ${theme.chipText}`}>
            <Layers className="h-3.5 w-3.5" /> Guide
          </div>
          <h1 className="font-serif text-4xl tracking-tight text-foreground md:text-5xl">
            The Polyvagal Ladder
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-muted-foreground">
            Ventral, sympathetic, dorsal — a kind map of where your body goes, and
            how it finds its way back.
          </p>
          <div className="mt-4 flex justify-center">
            <BookmarkButton id="learn:polyvagal-ladder" title="Polyvagal Ladder" href="/learn/polyvagal-ladder" section="Learn" />
          </div>
        </header>

        <LearnCarousel cards={cards} theme={theme} storageKey="polyvagal" />

        <p className="mt-10 rounded-2xl border border-border bg-muted/30 p-5 text-center text-xs leading-relaxed text-muted-foreground">
          Educational only. If you feel unsafe, please reach out to <Link to="/crisis" className="text-gold underline-offset-2 hover:underline">Crisis Resources</Link>.
        </p>
      </div>
    </main>
  );
}
